import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircle2, Info, Loader2, X } from 'lucide-react'
import { AuthSplitShell } from '@/components/auth/AuthSplitShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { authApi } from '@/services/authApi'
import { getApiErrorMessage } from '@/services/api'
import {
  forgotPasswordSchema,
  type ForgotPasswordFormData,
} from '@/lib/validations/auth'

export default function ForgotPasswordPage() {
  // Same confirmation regardless of whether the email exists (AC-2.1.2-004/025/026).
  const { t } = useTranslation('auth')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onSubmit',
    defaultValues: { email: '' },
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setSubmitting(true)
    setServerError(null)
    try {
      await authApi.forgotPassword({ email: data.email })
      setSubmittedEmail(data.email)
      setSent(true)
    } catch (err) {
      // AC-2.1.2-027: send service unreachable.
      setServerError(getApiErrorMessage(err, t('forgot.sendError')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthSplitShell rightPaneClassName='py-12'>
      <div className='space-y-6'>
        <div className='space-y-1.5'>
          <h1 className='font-heading text-[24px] font-semibold leading-[1.2] tracking-[-0.02em] text-[#1f2329]'>
            {t('forgot.title')}
          </h1>
          <p className='text-sm leading-6 text-[#5f6470]'>
            {t('forgot.description')}
          </p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className='flex flex-col gap-6'
        >
          {serverError && (
            <div
              role='alert'
              className='relative rounded-2xl border border-[#f4b7ac] bg-[#fff3f0] p-4'
            >
              <div className='flex gap-3'>
                <Info
                  className='mt-0.5 h-5 w-5 shrink-0 text-[#900B09]'
                  aria-hidden
                />
                <div className='flex min-w-0 flex-1 flex-col gap-1 pr-8'>
                  <p className='text-base font-semibold leading-[1.4] text-[#900B09]'>
                    {t('login.errorTitle')}
                  </p>
                  <p className='text-base font-normal leading-[1.4] text-[#900B09]'>
                    {serverError}
                  </p>
                </div>
              </div>
              <button
                type='button'
                onClick={() => setServerError(null)}
                className='absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-[#900B09] hover:bg-[#EC221F]/10'
                aria-label={t('login.dismissError')}
              >
                <X className='h-4 w-4' />
              </button>
            </div>
          )}

          <div className='flex flex-col gap-1'>
            <Label
              htmlFor='email'
              className='text-[11px] font-medium uppercase tracking-[0.08em] text-[#4e5562]'
            >
              {t('forgot.emailLabel')}
            </Label>
            <Input
              id='email'
              type='email'
              autoComplete='email'
              maxLength={100}
              placeholder={t('login.emailPlaceholder')}
              aria-invalid={!!errors.email}
              className={cn(
                'h-11 rounded-xl border border-transparent bg-[#f5f7fb] px-4 py-3 text-sm text-[#20242b] shadow-none placeholder:text-[#b5bcc8] focus-visible:border-[#FF5A1F]/30 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#FF5A1F]/12 focus-visible:ring-offset-0',
                errors.email &&
                  'border-[#EC221F] focus-visible:border-[#EC221F] focus-visible:ring-[#EC221F]/12'
              )}
              {...register('email')}
            />
            {errors.email && (
              <p className='text-sm font-normal leading-[1.4] text-[#EC221F]'>
                {errors.email.message}
              </p>
            )}
          </div>

          <div className='flex flex-col gap-4 pt-1'>
            <Button
              type='submit'
              className='h-11 w-full rounded-xl border border-[#FF5A1F] bg-[#FF5A1F] px-4 text-base font-medium text-white shadow-[0_12px_24px_rgba(255,90,31,0.2)] transition-transform hover:translate-y-[-1px] hover:bg-[#FF5A1F]/92'
              disabled={submitting}
            >
              {submitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              {t('forgot.submit')}
            </Button>

            <Button
              variant='ghost'
              className='h-auto min-h-0 w-full rounded-lg px-3 py-1 text-base font-medium leading-none text-[#FF5A1F] hover:bg-transparent hover:text-[#FF5A1F]/80'
              asChild
            >
              <Link to='/login'>{t('forgot.backToLogin')}</Link>
            </Button>

            {sent && (
              <div className='flex items-start gap-2.5 p-3.5 text-success'>
                <CheckCircle2 className='mt-0.5 h-5 w-5 shrink-0' />
                <p className='text-[14px] leading-5 font-medium'>
                  {t('forgot.confirmation', {
                    email: submittedEmail,
                  })}
                </p>
              </div>
            )}
          </div>
        </form>
      </div>
    </AuthSplitShell>
  )
}
